import { WorkoutSolverOutcome, WorkoutSolverResults, WorkoutSolverSolved } from '@cf-shared/workout-solver-interface';

/**
 * What the app does when the workout solver gives no program, and how a caller recognises why.
 *
 * The solver's reply is a union on `outcome` (#5942): only a solved reply carries a program. A
 * refused reply means the user is not on a paid plan, which the user can fix by subscribing, so
 * it gets an error class of its own. Before #5942 the intake form recognised it by finding
 * "paid users only" in the error text; the class replaces that text match, and keeps the same
 * wording in its message so nothing that still reads the text changes.
 *
 * Kept free of Angular so a unit test can drive it (`workout-solver-failure.test.ts`), the same
 * way `plan-check-failure.ts` is.
 */

/** Thrown when the workout solver refuses because the user is not on a paid plan. */
export class WorkoutSolverRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkoutSolverRefusedError';
    // restores the prototype chain across the ES5 target's Error subclassing, so `instanceof`
    // below answers correctly in the built bundle as well as under ts-node
    Object.setPrototypeOf(this, WorkoutSolverRefusedError.prototype);
  }
}

/** True when `err` is the paid-feature refusal. Never test the message text. */
export function isWorkoutSolverRefused(err: unknown): err is WorkoutSolverRefusedError {
  return err instanceof WorkoutSolverRefusedError;
}

/**
 * Hand back the program, or throw when there is none. `whatFailed` starts the thrown message, for
 * example "Can't solve workout". A failure throws a plain `Error`, a refusal a
 * `WorkoutSolverRefusedError`; both messages read exactly as they did before #5942.
 */
export function requireWorkoutProgram(result: WorkoutSolverResults, whatFailed: string): WorkoutSolverSolved {
  switch (result.outcome) {
    case WorkoutSolverOutcome.Solved:
      return result;
    case WorkoutSolverOutcome.Refused:
      throw new WorkoutSolverRefusedError(`${whatFailed}: ${result.error}`);
    case WorkoutSolverOutcome.Failed:
      throw new Error(`${whatFailed}: ${result.error}`);
    default:
      // `readWorkoutSolverReply` never lets an unknown outcome through; if one gets here anyway,
      // it must not read as a program
      throw new Error(`${whatFailed}: unknown outcome '${String((result as { outcome?: unknown }).outcome)}'`);
  }
}
